import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getPedidosByStatusSimples,
    getPedidosByStatusFinalizados,
    updateStatusPedido,
    updateStatusPedidoFinalizado
} from '../services/api';
import { toast } from 'react-toastify';
import { useEmpresa } from '../contexts/empresaContext';

const PEDIDO_STATUS = {
    ANALISE: '1',
    PRODUCAO: '2',
    PRONTO: '3',
    FINALIZADO: '4'
};

const usePedidos = (userID, idEmpresa, vinculo_empresa) => {
    const { t } = useTranslation('orders');
    const [pedidosSimples, setPedidosSimples] = useState([]);
    const [pedidosFinalizados, setPedidosFinalizados] = useState([]);
    const [originalPedidos, setOriginalPedidos] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    // ✅ NOVO: Estado para rastrear pedidos em processo de atualização
    const [pedidosProcessando, setPedidosProcessando] = useState(new Set());

    // ✅ Usar contexto de empresa para cache
    const { empresaConfig: empresaConfigContext, fetchEmpresaConfig } = useEmpresa();

    // Configurações da empresa (com fallback para compatibilidade)
    const empresaConfig = empresaConfigContext || {
        tipoImpressao: '',
        tempoBalcaoMin: 0,
        tempoBalcaoMax: 0,
        tempoEntregaMin: 0,
        tempoEntregaMax: 0,
        hasCancelPassword: false
    };

    // Função para buscar pedidos simples (status 1, 2, 3)
    const fetchPedidosSimples = useCallback(async () => {
        try {
            // ✅ REMOVIDO: getVinculoEmpresa() - agora vem do contexto
            // ✅ OTIMIZAÇÃO: Uma única chamada ao invés de duas paralelas
            const pedidosResponse = await getPedidosByStatusSimples(userID, idEmpresa, vinculo_empresa, ['1', '2', '3'], 'false');

            // ✅ Garantir que empresaConfig está carregado (se ainda não estiver)
            if (!empresaConfigContext) {
                await fetchEmpresaConfig();
            }

            return pedidosResponse.data.pedidos;
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao buscar pedidos simples:', error);
            toast(t('loadError'), { type: 'error' });
            return [];
        }
    }, [userID, idEmpresa, vinculo_empresa, empresaConfigContext, fetchEmpresaConfig]);

    // Função para buscar pedidos finalizados (status 4)
    const fetchPedidosFinalizados = useCallback(async () => {
        try {
            const response = await getPedidosByStatusFinalizados(userID, idEmpresa, vinculo_empresa, 'false');
            return response.data.pedidos;
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao buscar pedidos finalizados:', error);
            toast(t('loadFinalizedError'), { type: 'error' });
            return [];
        }
    }, [userID, idEmpresa, vinculo_empresa]);

    // Função para carregar todos os pedidos
    const loadPedidos = useCallback(async () => {
        setIsLoading(true);
        try {
            const [simplesData, finalizadosData] = await Promise.all([
                fetchPedidosSimples(),
                fetchPedidosFinalizados()
            ]);


            setPedidosSimples(simplesData);
            setPedidosFinalizados(finalizadosData);
            setOriginalPedidos([...simplesData, ...finalizadosData]);
        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao carregar pedidos:', error);
        } finally {
            setIsLoading(false);
        }
    }, [fetchPedidosSimples, fetchPedidosFinalizados]);

    // ⚡ SOLUÇÃO DEFINITIVA: Atualização instantânea tipo Trello
    const updatePedidoLocally = useCallback((_id, newStatus, finalizadoAt) => {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 🚀 Movendo pedido ${_id} para status ${newStatus}`);

        // 🔍 PASSO 1: Buscar pedido nos estados ATUAIS (não dentro de setState!)
        const pedidoEmSimples = pedidosSimples.find(p => p._id === _id);
        const pedidoEmFinalizados = pedidosFinalizados.find(p => p._id === _id);
        const pedidoAtual = pedidoEmSimples || pedidoEmFinalizados;

        if (!pedidoAtual) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error(`[OPTIMISTIC] ❌ ERRO CRÍTICO: Pedido ${_id} não encontrado!`);
            return;
        }

        // 📦 PASSO 2: Criar pedido atualizado
        const pedidoAtualizado = {
            ...pedidoAtual,
            status_pedido: newStatus,
            ...(finalizadoAt && { finalizadoAt })
        };

        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 📍 Pedido encontrado em: ${pedidoEmSimples ? 'Simples' : 'Finalizados'}`);
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 🎯 Movendo de status ${pedidoAtual.status_pedido} → ${newStatus}`);

        // 🚀 PASSO 3: ATUALIZAR TODOS OS ARRAYS DE UMA VEZ (Batched)

        if (newStatus === PEDIDO_STATUS.FINALIZADO) {
            // ✅ MOVER PARA FINALIZADOS
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] ➡️ Movendo para FINALIZADOS`);

            // Remove dos simples
            setPedidosSimples(prev => {
                const filtered = prev.filter(p => p._id !== _id);
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 📊 Simples: ${prev.length} → ${filtered.length} pedidos`);
                return filtered;
            });

            // Adiciona aos finalizados
            setPedidosFinalizados(prev => {
                const updated = [...prev, pedidoAtualizado];
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 📊 Finalizados: ${prev.length} → ${updated.length} pedidos`);
                return updated;
            });

        } else {
            // ✅ MOVER ENTRE SIMPLES (Análise → Produção → Pronto)
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 🔄 Atualizando dentro de SIMPLES`);

            setPedidosSimples(prev => {
                // Se estava em finalizados, adiciona aos simples
                if (pedidoEmFinalizados) {
                    const updated = [...prev, pedidoAtualizado];
                    process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 📊 Simples: ${prev.length} → ${updated.length} pedidos (vindo de finalizados)`);
                    return updated;
                }
                // Se estava em simples, apenas atualiza
                const mapped = prev.map(p => p._id === _id ? pedidoAtualizado : p);
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 📊 Simples: ${prev.length} pedidos (atualizado)`);
                return mapped;
            });

            // Remove dos finalizados se estava lá
            if (pedidoEmFinalizados) {
                setPedidosFinalizados(prev => {
                    const filtered = prev.filter(p => p._id !== _id);
                    process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 📊 Finalizados: ${prev.length} → ${filtered.length} pedidos`);
                    return filtered;
                });
            }
        }

        // 🔄 PASSO 4: Sincronizar array original
        setOriginalPedidos(prev => {
            const updated = prev.map(p => p._id === _id ? pedidoAtualizado : p);
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] 📊 Originais sincronizados: ${updated.length} pedidos`);
            return updated;
        });

        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[OPTIMISTIC] ✅ COMPLETO! Pedido ${_id} movido para status ${newStatus}`);
    }, [pedidosSimples, pedidosFinalizados]);

    // ✅ OPTIMISTIC UPDATE: Função para avançar pedido com atualização instantânea
    const avancarPedido = useCallback(async (_id, id_pedido, status_pedido) => {
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 🎬 Iniciando: ${_id} (status atual: ${status_pedido})`);

        // 🛡️ PROTEÇÃO: Evitar cliques duplos
        if (pedidosProcessando.has(_id)) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.warn(`[AVANCAR] ⚠️ Pedido ${_id} já está sendo processado! Ignorando clique duplo.`);
            toast.warning(t('alreadyProcessing'), {
                autoClose: 1000,
                position: "top-center"
            });
            return;
        }

        let newStatus = "";
        let finalizadoAt = "";
        let isMesaOrder = false;

        // Determinar novo status
        switch (status_pedido) {
            case PEDIDO_STATUS.ANALISE:
                newStatus = PEDIDO_STATUS.PRODUCAO;
                break;
            case PEDIDO_STATUS.PRODUCAO:
                newStatus = PEDIDO_STATUS.PRONTO;
                break;
            case PEDIDO_STATUS.PRONTO: {
                const pedidoAtual = [...pedidosSimples, ...pedidosFinalizados].find(p => p._id === _id);

                if (pedidoAtual && pedidoAtual.origem_pedido === 'mesa') {
                    // Pedidos de mesa: backend valida se mesa está livre (órfão) ou ocupada.
                    // Não fazemos optimistic update para evitar glitch visual no rollback.
                    isMesaOrder = true;
                } else {
                    // Validar forma de pagamento antes de finalizar (apenas pedidos não-mesa)
                    if (pedidoAtual && !pedidoAtual.tipo_pagamento) {
                        toast.error(t('payment.required'), {
                            autoClose: 4000,
                            position: "top-center"
                        });
                        return;
                    }
                }

                newStatus = PEDIDO_STATUS.FINALIZADO;
                finalizadoAt = new Date();
                break;
            }
            default:
                process.env.REACT_APP_ISDEVELOP === 'true' && console.warn(`[AVANCAR] ⚠️ Status inválido: ${status_pedido}`);
                return;
        }

        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 🎯 Novo status será: ${newStatus}`);

        // 📸 SNAPSHOT: Guardar estado antes da mudança (para rollback)
        const snapshotSimples = [...pedidosSimples];
        const snapshotFinalizados = [...pedidosFinalizados];
        const snapshotOriginais = [...originalPedidos];

        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 📸 Snapshot criado:`, {
            simples: snapshotSimples.length,
            finalizados: snapshotFinalizados.length,
            originais: snapshotOriginais.length
        });

        // 🎯 PASSO 1: Marcar como processando (feedback visual)
        setPedidosProcessando(prev => new Set([...prev, _id]));
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 🔄 Marcado como processando`);

        // ⚡ PASSO 2: ATUALIZAR UI IMEDIATAMENTE (Optimistic Update)
        // Pedidos de mesa: NÃO fazer optimistic update - esperar backend validar se mesa está livre
        if (!isMesaOrder) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] ⚡ Atualizando UI instantaneamente...`);
            updatePedidoLocally(_id, newStatus, finalizadoAt);
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] ✨ UI atualizada!`);
        } else {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 🍽️ Pedido de mesa - aguardando validação do backend...`);
        }

        // 🔄 PASSO 3: Fazer requisição em background
        process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 🌐 Enviando requisição para servidor...`);
        try {
            let response;
            if (status_pedido === PEDIDO_STATUS.PRONTO) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 📤 Chamando updateStatusPedidoFinalizado...`);
                response = await updateStatusPedidoFinalizado(userID, _id, id_pedido, newStatus, finalizadoAt);
            } else {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 📤 Chamando updateStatusPedido...`);
                response = await updateStatusPedido(userID, _id, id_pedido, newStatus);
            }

            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] ✅ Resposta do servidor:`, response.data);

            // Pedido de mesa confirmado: atualizar UI agora que backend validou
            if (isMesaOrder) {
                updatePedidoLocally(_id, newStatus, finalizadoAt);
            }

            // ✅ SUCESSO: Remover da lista de processamento
            setPedidosProcessando(prev => {
                const next = new Set(prev);
                next.delete(_id);
                return next;
            });

            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 🎉 Sucesso! Pedido confirmado no servidor`);

            // Toast de sucesso (discreto)
            toast(response.data.msg || "Status atualizado!", {
                autoClose: 1500,
                type: "success",
                position: "bottom-right"
            });

        } catch (err) {
            // ❌ ERRO: ROLLBACK - Reverter para estado anterior
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('[AVANCAR] ❌ ERRO! Revertendo mudanças...', err);

            // Rollback apenas se fez optimistic update (pedidos não-mesa)
            if (!isMesaOrder) {
                setPedidosSimples(snapshotSimples);
                setPedidosFinalizados(snapshotFinalizados);
                setOriginalPedidos(snapshotOriginais);
                process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 🔙 Estado revertido para snapshot`);
            }

            // Remover da lista de processamento
            setPedidosProcessando(prev => {
                const next = new Set(prev);
                next.delete(_id);
                return next;
            });

            // Toast de erro com mensagem específica do backend
            const errorMsg = err.response?.data?.msg || t('advanceError');
            toast(errorMsg, {
                autoClose: isMesaOrder ? 5000 : 3000,
                type: isMesaOrder ? "warning" : "error",
                position: "top-center"
            });

            process.env.REACT_APP_ISDEVELOP === 'true' && console.log(`[AVANCAR] 💥 Operação cancelada`);
        }
    }, [userID, pedidosSimples, pedidosFinalizados, originalPedidos, pedidosProcessando, updatePedidoLocally]);

    // Atualizar pedidos via WebSocket
    const updateFromWebSocket = useCallback(async () => {
        const [simplesData, finalizadosData] = await Promise.all([
            fetchPedidosSimples(),
            fetchPedidosFinalizados()
        ]);

        setPedidosSimples(simplesData);
        setPedidosFinalizados(finalizadosData);
        setOriginalPedidos([...simplesData, ...finalizadosData]);
    }, [fetchPedidosSimples, fetchPedidosFinalizados]);

    // Pedidos organizados por status usando useMemo para otimização
    const pedidosOrganizados = useMemo(() => {
        // Função para ordenar pedidos do mais antigo para o mais recente
        const ordenarPorData = (pedidos) => {
            return [...pedidos].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        };

        const organizados = {
            analise: ordenarPorData(pedidosSimples.filter(p => p.status_pedido === PEDIDO_STATUS.ANALISE)),
            producao: ordenarPorData(pedidosSimples.filter(p => p.status_pedido === PEDIDO_STATUS.PRODUCAO)),
            pronto: ordenarPorData(pedidosSimples.filter(p => p.status_pedido === PEDIDO_STATUS.PRONTO)),
            finalizado: ordenarPorData(pedidosFinalizados.filter(p => p.status_pedido === PEDIDO_STATUS.FINALIZADO))
        };


        return organizados;
    }, [pedidosSimples, pedidosFinalizados]);

    // Filtrar pedidos por tipo
    const filtrarPedidos = useCallback((filtro) => {
        // Função para ordenar pedidos do mais antigo para o mais recente
        const ordenarPorData = (pedidos) => {
            return [...pedidos].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        };

        let filteredSimples = [...originalPedidos.filter(p =>
            p.status_pedido === PEDIDO_STATUS.ANALISE ||
            p.status_pedido === PEDIDO_STATUS.PRODUCAO ||
            p.status_pedido === PEDIDO_STATUS.PRONTO
        )];

        let filteredFinalizados = [...originalPedidos.filter(p =>
            p.status_pedido === PEDIDO_STATUS.FINALIZADO
        )];

        if (filtro === "delivery") {
            filteredSimples = filteredSimples.filter(p => p.entrega?.tipo_entrega?.toLowerCase() === "entrega");
            filteredFinalizados = filteredFinalizados.filter(p => p.entrega?.tipo_entrega?.toLowerCase() === "entrega");
        } else if (filtro === "balcao") {
            filteredSimples = filteredSimples.filter(p => p.entrega?.tipo_entrega?.toLowerCase() === "retirada");
            filteredFinalizados = filteredFinalizados.filter(p => p.entrega?.tipo_entrega?.toLowerCase() === "retirada");
        } else if (filtro === "mesas") {
            filteredSimples = filteredSimples.filter(p => p.origem_pedido === 'mesa');
            filteredFinalizados = filteredFinalizados.filter(p => p.origem_pedido === 'mesa');
        }

        // Aplicar ordenação antes de definir os estados
        setPedidosSimples(ordenarPorData(filteredSimples));
        setPedidosFinalizados(ordenarPorData(filteredFinalizados));
    }, [originalPedidos]);

    // Buscar pedidos por query
    const buscarPedidos = useCallback((searchQuery) => {
        // Função para ordenar pedidos do mais antigo para o mais recente
        const ordenarPorData = (pedidos) => {
            return [...pedidos].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        };

        if (!searchQuery.trim()) {
            // Se não há busca, restaurar pedidos originais
            const simplesOriginais = originalPedidos.filter(p =>
                p.status_pedido === PEDIDO_STATUS.ANALISE ||
                p.status_pedido === PEDIDO_STATUS.PRODUCAO ||
                p.status_pedido === PEDIDO_STATUS.PRONTO
            );
            const finalizadosOriginais = originalPedidos.filter(p =>
                p.status_pedido === PEDIDO_STATUS.FINALIZADO
            );

            setPedidosSimples(ordenarPorData(simplesOriginais));
            setPedidosFinalizados(ordenarPorData(finalizadosOriginais));
            return;
        }

        const query = searchQuery.toLowerCase();
        const filteredPedidos = originalPedidos.filter(p =>
            p.nome_cliente.toLowerCase().includes(query) ||
            p.id_pedido_counter?.toString().includes(query)
        );

        const filteredSimples = filteredPedidos.filter(p =>
            p.status_pedido === PEDIDO_STATUS.ANALISE ||
            p.status_pedido === PEDIDO_STATUS.PRODUCAO ||
            p.status_pedido === PEDIDO_STATUS.PRONTO
        );

        const filteredFinalizados = filteredPedidos.filter(p =>
            p.status_pedido === PEDIDO_STATUS.FINALIZADO
        );

        setPedidosSimples(ordenarPorData(filteredSimples));
        setPedidosFinalizados(ordenarPorData(filteredFinalizados));
    }, [originalPedidos]);

    return {
        pedidosOrganizados,
        empresaConfig,
        isLoading,
        isUpdating,
        pedidosProcessando,  // ✅ NOVO: Para feedback visual
        loadPedidos,
        avancarPedido,
        updateFromWebSocket,
        filtrarPedidos,
        buscarPedidos
    };
};

export default usePedidos;
